import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseURL } from "../../API/API";
import Header from "../../Components/common/Header";
import Footer from "../../Components/common/Footer";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";
import { FaRegCalendarAlt, FaRegClock } from "react-icons/fa";
import "./TeacherProfile.css";
import { getCoursePackageTypeLabel } from "../../utils/packageLabels";
import LogoNohe from "../../Imge/LogoNohe.png";

const fallbackImage = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3";

export default function TeacherProfile() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [teacher, setTeacher] = useState(null);
  const [courses, setCourses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const isAdminRoute = String(teacherId) === "admin";
        const [teacherRes, coursesRes, packagesRes] = await Promise.all([
          axios.get(`${baseURL}/learning/teachers/${teacherId}`),
          axios.get(`${baseURL}/learning/teachers/${teacherId}/chapters`),
          isAdminRoute ? Promise.resolve({ data: { data: [] } }) : axios.get(`${baseURL}/learning/packages/list`, { params: { teacher_id: teacherId } }),
        ]);
        const teacherData = teacherRes.data?.data || null;
        const isAdminTeacher = isAdminRoute || teacherData?.is_admin_teacher;
        setTeacher(teacherData);
        setCourses(isAdminTeacher ? (coursesRes.data?.data || []) : (coursesRes.data?.data || []).filter((course) => Number(course.teacher_id) === Number(teacherId)));
        setPackages(isAdminTeacher ? [] : (packagesRes.data?.data || []).filter((pkg) => Number(pkg.teacher_id) === Number(teacherId)));
      } catch (err) {
        console.error("Error fetching teacher profile:", err);
        toast.error(err.response?.data?.message || "حدث خطأ أثناء تحميل ملف المدرس");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [teacherId]);

  const ensureCourseAccess = (stageId) => {
    if (!auth?.accessToken) {
      toast.info("يرجى إنشاء حساب أو تسجيل الدخول أولا");
      navigate("/register", { state: { selectedStageId: stageId } });
      return false;
    }
    if (auth.user?.is_staff) return true;
    if (Number(stageId) !== Number(auth.user?.academic_year_id)) {
      toast.error("يمكنك الدخول إلى محتوى السنة الدراسية المسجلة في حسابك فقط.");
      return false;
    }
    return true;
  };

  const handleCourseClick = (course) => {
    const stageId = course.academic_year_id;
    if (!ensureCourseAccess(stageId)) return;
    navigate(`/learn/${stageId}?teacher_id=${teacherId}`);
  };

  const handleSubscribeClick = (course) => {
    const stageId = course.academic_year_id;
    if (!ensureCourseAccess(stageId)) return;
    navigate(`/invoice/${course.id}`, {
      state: {
        price: Number(course.price ?? 0),
        discount: Number(course.discount ?? 0),
        courseName: course.name,
      },
    });
  };

  const handlePackageSubscribe = (pkg) => {
    if (!auth?.accessToken) {
      toast.info("يرجى إنشاء حساب أو تسجيل الدخول للاشتراك");
      navigate("/register", { state: { selectedStageId: pkg.academic_year_id } });
      return;
    }
    if (!auth.user?.is_staff && Number(pkg.academic_year_id) !== Number(auth.user?.academic_year_id)) {
      toast.error("هذه الباقة ليست لسنتك الدراسية المسجلة.");
      return;
    }
    navigate(`/invoice/package/${pkg.id}`);
  };

  const renderPackageCard = (pkg) => {
    const packageLabel = getCoursePackageTypeLabel(pkg);
    const finalPrice = Math.max(Number(pkg.price || 0) - Number(pkg.discount || 0), 0);
    return (
      <div key={pkg.id} className="col-12">
        <div className="bg-white border shadow-sm overflow-hidden" style={{ borderRadius: 8 }}>
          <div className="row g-0">
            <div className="col-md-5">
              <div style={{ minHeight: 230, height: "100%", background: pkg.image ? `url(${pkg.image}) center/cover no-repeat` : "linear-gradient(135deg, #0f766e, #111827)" }}>
                <span className="badge bg-warning text-dark m-3">{packageLabel}</span>
              </div>
            </div>
            <div className="col-md-7">
              <div className="p-4 text-end h-100 d-flex flex-column">
                <div className="d-flex gap-2 flex-wrap justify-content-end mb-2">
                  <span className="badge bg-light text-dark border">{pkg.academic_year_name}</span>
                  <span className="badge bg-light text-dark border">{pkg.chapters_count} شهر</span>
                </div>
                <h3 className="fw-bold mb-2" style={{ color: "#0f172a", fontSize: "1.35rem" }}>{pkg.name}</h3>
                <p className="text-muted" style={{ lineHeight: 1.7 }}>{pkg.description || `باقة ${packageLabel} تفتح الشهور المختارة مع ${teacher?.name}.`}</p>
                <div className="mt-auto">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: "10px" }}>
                    <div className="text-end">
                      <div className="small text-muted">السعر الموحد:</div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fs-4 fw-bold text-primary">{finalPrice.toFixed(0)} ج.م</span>
                        {Number(pkg.discount || 0) > 0 && (
                          <span className="text-decoration-line-through text-muted small">{Number(pkg.price || 0).toFixed(0)} ج.م</span>
                        )}
                      </div>
                    </div>
                    {Number(pkg.discount || 0) > 0 && (
                      <span className="badge bg-danger-light text-danger fs-6 py-2 px-3 rounded-pill">
                        وفر {Number(pkg.discount || 0).toFixed(0)} ج.م
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary fw-bold" onClick={() => navigate(`/packages/${pkg.id}`)}>التفاصيل</button>
                    <button className="btn fw-bold text-white" style={{ backgroundColor: "#14b8a6" }} onClick={() => handlePackageSubscribe(pkg)}>اشترك الآن</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCourseCard = (course) => {
    const finalPrice = Math.max(Number(course.price || 0) - Number(course.discount || 0), 0);
    return (
      <div key={course.id} className="col-12 col-md-6">
        <div className="suggested-course-card stage-card w-100 d-flex flex-column" style={{ position: "relative", marginTop: 25, height: "100%", borderRadius: 20, overflow: "hidden", border: "1.5px solid #cbd5e1", backgroundColor: "#fff" }}>
          <div style={{ position: "relative", width: "100%", height: 220, overflow: "hidden" }}>
            <img src={course.image_path || fallbackImage} alt={course.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div className="text-white text-center px-3 py-1 fw-bold" style={{ position: "absolute", bottom: 12, right: 12, borderRadius: 6, fontSize: "0.8rem", backgroundColor: "#14b8a6" }}>شهر دراسي</div>
          </div>
          <div className="p-4 d-flex flex-column text-center flex-grow-1">
            <div className="d-flex justify-content-center mb-3">
              <div className="fw-bold px-3 py-1" style={{ backgroundColor: "#eab308", color: "#000", borderRadius: 8, fontSize: "0.85rem" }}>{teacher?.subject} - {course.academic_year_name}</div>
            </div>
            <h3 className="text-dark" style={{ fontSize: "1.15rem", fontWeight: 800, marginBottom: 12, lineHeight: 1.4 }}>{course.name}</h3>
            <p className="text-muted" style={{ fontSize: "0.88rem", lineHeight: 1.6, minHeight: 45 }}>{course.description || "سيتم إضافة تفاصيل هذا الشهر قريبا."}</p>
            <div className="d-flex gap-3 justify-content-center mt-auto pt-3">
              <button onClick={() => handleCourseClick(course)} className="py-2 px-4 text-center" style={{ border: "2px solid #14b8a6", color: "#14b8a6", backgroundColor: "transparent", borderRadius: 30, fontWeight: "bold", flex: 1 }}>الدخول للكورس</button>
              <button onClick={() => handleSubscribeClick(course)} className="py-2 px-4 text-center" style={{ backgroundColor: "#14b8a6", color: "#fff", border: "none", borderRadius: 30, fontWeight: "bold", flex: 1 }}>اشترك الآن</button>
            </div>
            <div style={{ height: 1, backgroundColor: "#e2e8f0", margin: "16px 0 12px" }} />
            <div className="d-flex justify-content-between align-items-center" style={{ direction: "rtl" }}>
              <div className="d-flex flex-column gap-1 text-end text-muted" style={{ fontSize: "0.78rem" }}>
                <div className="d-flex align-items-center gap-1"><FaRegCalendarAlt style={{ color: "#14b8a6" }} /><span>متاح الآن</span></div>
                <div className="d-flex align-items-center gap-1"><FaRegClock style={{ color: "#14b8a6" }} /><span>دروس مسجلة</span></div>
              </div>
              <div className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: "#14b8a6", color: "#fff", borderRadius: 8, padding: "4px 10px", fontSize: "0.85rem" }}>
                <span style={{ backgroundColor: "#fff", color: "#0f172a", borderRadius: 4, padding: "2px 6px", fontWeight: "bold" }}>{finalPrice.toFixed(0)}</span>
                <span>جنيه</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <><Header /><div className="text-center py-5"><h3>جاري تحميل ملف المدرس...</h3></div><Footer /></>;
  }

  if (!teacher) {
    return <><Header /><div className="text-center py-5"><h3>المدرس غير موجود</h3></div><Footer /></>;
  }

  const hasContent = courses.length > 0 || packages.length > 0;

  return (
    <>
      <Header />
      <div className="teacher-profile-page px-3 lg:px-5 py-10" dir="rtl" style={{ backgroundColor: "#f8fafc" }}>
        <div className="rounded-md py-12 px-4 text-slate-100 relative overflow-hidden pb-40" style={{ backgroundImage: "linear-gradient(135deg, #38bdf8, #0ea5e9)", borderRadius: 12, minHeight: 300, position: "relative", overflow: "hidden" }}>
          <div className="relative z-10 d-flex flex-column gap-3">
            <div className="mt-3"><h1 className="text-white" style={{ fontSize: "2.6rem", fontWeight: 900, margin: 0 }}>{teacher.name}</h1><p className="text-slate-200 mt-1" style={{ fontSize: "1.45rem", margin: 0 }}>{teacher.subject}</p></div>
            <div className="d-flex mt-2"><div className="rounded-full px-4 py-2 text-white" style={{ backgroundColor: "#0284c7", borderRadius: 30, fontWeight: 600, fontSize: "0.95rem" }}>{teacher.subject} - {teacher.name}</div></div>
          </div>
        </div>

        <div className="container-fluid mt-4">
          <div className="row g-4 align-items-start">
            <div className="col-12 col-lg-8 order-2 order-lg-1">
              {!hasContent && <div className="bg-white border text-center p-5 shadow-sm" style={{ borderRadius: 8, color: "#64748b", fontWeight: 700 }}>سيتم إضافتها قريبا</div>}

              {packages.length > 0 && <div className="mb-5 px-2 py-4"><SectionTitle title="باقات الترم والسنة" /><div className="row g-4">{packages.map(renderPackageCard)}</div></div>}
              {courses.length > 0 && <div className="mb-5 px-2 py-4"><SectionTitle title="الاشتراك الشهري للمدرس" /><div className="row g-4">{courses.map(renderCourseCard)}</div></div>}
            </div>

            <div className="col-12 col-lg-4 order-1 order-lg-2" style={{ marginTop: "-120px", zIndex: 12 }}>
              <div className="w-full glassy smooth clr-text-primary shadow-large rounded-lg overflow-hidden bg-white" style={{ borderRadius: 16, border: "1px solid #cbd5e1", overflow: "hidden" }}>
                <div className="p-4">
                  <div className="overflow-hidden" style={{ borderRadius: 16, border: "4px solid #38bdf8", boxShadow: "0 4px 10px rgba(0,0,0,0.05)" }}>
                    <img src={teacher.profile_image || (teacher.is_admin_teacher ? LogoNohe : "https://cdn-icons-png.flaticon.com/512/194/194931.png")} alt={teacher.name} className="w-100" style={{ height: "auto", maxHeight: 400, objectFit: "cover" }} />
                  </div>
                  <div className="font-small mt-3">
                    <div className="py-3 d-flex justify-content-between border-bottom"><span className="fw-bold">المادة</span><span className="text-muted">{teacher.subject || "غير محدد"}</span></div>
                    <div className="py-3 d-flex justify-content-between"><span className="fw-bold">عدد المحتوى</span><span className="text-muted">{courses.length + packages.length}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

function SectionTitle({ title }) {
  return (
    <div className="mb-4" style={{ borderBottom: "2px solid #e2e8f0", paddingBottom: 12, position: "relative" }}>
      <h2 style={{ fontSize: "1.75rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>{title}</h2>
      <div style={{ position: "absolute", bottom: -2, right: 0, width: 120, height: 3, backgroundColor: "#0ea5e9", borderRadius: 2 }} />
    </div>
  );
}